/**
 * Hook for managing player card base functionality with Apollo-based tag management
 * @remarks
 * This hook provides the core functionality for player cards including:
 * - Reading tags from Apollo cache (automatically reactive)
 * - Tag mutation operations via Apollo mutations
 * - UI state management (expanded tags, tag selector visibility)
 * - Event handlers for drag and drop, tag operations
 *
 * Migrated from localStorage + event emitter to Apollo Client for better data consistency.
 * @example
 * ```tsx
 * const {
 *   displayedTags,
 *   currentTagIds,
 *   handleTagsChanged,
 *   isLoading,
 *   showTagSelector,
 *   setShowTagSelector,
 *   handleAddTag,
 *   handleRemoveTag
 * } = usePlayerCardBase({
 *   playerId: 'player-123',
 *   boardId: 'board-456',
 *   canEdit: true
 * });
 * ```
 */

import { type ApolloError, useQuery, useReactiveVar } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";

import { usePlayerCardId } from "@/features/player-kanban/hooks/usePlayerCardId";
import { useTagMutations } from "@/features/player-kanban/hooks/useTagMutations";
import {
  populateFallbackColors,
  tagFallbackColorsVar,
} from "@/features/player-kanban/stores/kanbanReactiveVars";
import { DEFAULT_TAG_COLOR } from "@/features/player-kanban/utils/tagUtils";
import {
  ListKanbanBoardTagsDocument,
  type ListKanbanBoardTagsQuery,
} from "@/generated/graphql";

/**
 * Props interface for usePlayerCardBase hook
 */
export interface BasePlayerCardProps {
  playerId: string;
  playerName: string;
  archetype: string;
  age: number;
  gpr: number;
  contractValue: string;
  contractMonths: number | null;
  agentName: string;
  teamName: string;
  playerAgent?: string;
  initialTags?: string[];
  boardId?: string;
  isOptimistic?: boolean;
  onPress?: () => void;
  onTagAdded?: (playerId: string, tag: string) => void;
  onDragStart?: (dragData?: unknown, event?: unknown) => void;
  onDragEnd?: () => void;
  onAutoScroll?: (dragX: number) => void;
  onBoardRefresh?: () => void;
  bioData?: {
    customStats?: Record<string, unknown> | null;
    fairFee?: number | null;
    expectedFee?: number | null;
    [key: string]: unknown;
  };
}

/**
 * Input props for usePlayerCardBase hook
 */
type UsePlayerCardBaseProps = Pick<
  BasePlayerCardProps,
  | "playerId"
  | "initialTags"
  | "boardId"
  | "onPress"
  | "onTagAdded"
  | "onDragStart"
  | "onDragEnd"
  | "bioData"
> & { canEdit?: boolean };

/**
 * Return type for usePlayerCardBase hook
 */
interface UsePlayerCardBaseReturn {
  displayedTags: { name: string; color: string }[];
  currentTagIds: string[];
  expandedTagIndex: number | null;
  showAllTags: boolean;
  hasMoreTags: boolean;
  /**
   * Backward-compatible alias for `displayedTags`.
   * @remarks
   * Declared `string[]` until SE-5631, which was never true — it has always
   * been the same `{ name, color }` list as `displayedTags`. The lie only
   * typechecked because the tag source was untyped `any` before the card list
   * became a typed read.
   */
  tags: { name: string; color: string }[];
  showTagSelector: boolean;
  /**
   * The card-list read's error, if any.
   * @remarks
   * SE-5631: nothing renders this yet — surfacing an error state on the card
   * is a behaviour change and is tracked separately. It is BOUND rather than
   * discarded because the client sets `errorPolicy: "all"`, under which a
   * failed read RESOLVES instead of rejecting. An unbound `error` therefore
   * makes a failed fetch indistinguishable from "this player has no tags" —
   * the same defect shape as the discarded `_boardsError` SE-6491 fixed. The
   * failure is logged once by the board-level read in `useKanbanBoardQueries`
   * that owns the fetch; this hook deliberately does not log it again, since
   * it mounts once per card and would emit N copies of one failure.
   */
  error: ApolloError | undefined;
  setShowTagSelector: (show: boolean) => void;
  handlePress: () => void;
  handleAddTag: () => void;
  handleRemoveTag: (tagName: string) => void;
  handleTagsChanged: (tagIds?: string[]) => Promise<void>;
  handleToggleTagExpansion: (index: number) => void;
  handleToggleShowAllTags: () => void;
  handleDragStart: (dragData?: unknown, event?: unknown) => void;
  handleDragEnd: () => void;
  isLoading: boolean;
}

/**
 * Hook for managing player card base functionality with Apollo-based tag management
 * @param root0 - Hook props
 * @param root0.playerId - The player's ID
 * @param root0.initialTags - Initial tag names (optional override, currently unused)
 * @param root0.boardId - The kanban board ID
 * @param root0.canEdit - Whether the user can edit tags
 * @param root0.onPress - Handler for card press
 * @param root0.onTagAdded - Handler for tag addition
 * @param root0.onDragStart - Handler for drag start
 * @param root0.onDragEnd - Handler for drag end
 * @returns Object containing tag data, loading state, and event handlers
 */
export const usePlayerCardBase = ({
  playerId,
  initialTags: _initialTags = [],
  boardId,
  canEdit = true,
  onPress,
  onTagAdded,
  onDragStart,
  onDragEnd,
}: UsePlayerCardBaseProps): UsePlayerCardBaseReturn => {
  // UI state management
  const [expandedTagIndex, setExpandedTagIndex] = useState<number | null>(null);
  const [showAllTags, setShowAllTags] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);

  // Card id AND the card's tags come from ONE read of the board's card list.
  //
  // SE-5631: this used to be two `cache-and-network` reads per card — this
  // hook's own `ListKanbanCards` query plus the one inside `usePlayerCardId`.
  // Worse, this one omitted `$first`, so its variable set differed from every
  // other call site and Apollo normalized it into a SECOND cache entry that
  // could never be served by the board-level read (SE-5058 cache-key
  // fragmentation). Measured: 2 network `ListKanbanCards` per card on a
  // staggered mount. Both reads are now the single cache-first read below.
  const {
    cardTags: currentTags,
    isLoading: isLoadingCard,
    error,
  } = usePlayerCardId(playerId, boardId || "");

  // Query board tags to get default colors for organization tags
  // IMPORTANT: Variables must match useBoardTagColors exactly for cache consistency
  // Using cache-and-network ensures component re-renders when cache is updated by mutations
  const { data: boardTagsData } = useQuery(ListKanbanBoardTagsDocument, {
    variables: { kanbanBoardId: boardId || "", first: 100 },
    fetchPolicy: "cache-and-network",
    skip: !boardId,
  });

  // Get fallback colors for tags (used when no board-specific color exists)
  const fallbackColors = useReactiveVar(tagFallbackColorsVar);

  // Create a lookup object for board tag colors using immutable reduce pattern
  const boardTagColors = useMemo(() => {
    return (
      boardTagsData?.listKanbanBoardTags?.edges?.reduce(
        (
          colorObj: Record<string, string>,
          edge: ListKanbanBoardTagsQuery["listKanbanBoardTags"]["edges"][number]
        ) => {
          const tagId = edge.node.tag.id;
          const color = edge.node.color;
          return color ? { ...colorObj, [tagId]: color } : colorObj;
        },
        {} as Record<string, string>
      ) ?? {}
    );
  }, [boardTagsData]);

  // Get tag mutation functions
  const { addTagsToPlayer, removeTagsFromPlayer } = useTagMutations(
    boardId || ""
  );

  // Get displayed tags with colors (with fallback to board tag colors)
  const displayedTags = useMemo(() => {
    return currentTags.map(tag => ({
      name: tag.name,
      // Priority: 1. Board-specific color from tag, 2. Board tag color lookup, 3. Fallback color, 4. Default
      color:
        tag.color ||
        boardTagColors[tag.id] ||
        fallbackColors[tag.id] ||
        DEFAULT_TAG_COLOR,
    }));
  }, [currentTags, boardTagColors, fallbackColors]);

  // Get current tag IDs
  const currentTagIds = useMemo(() => {
    return currentTags.map((tag: { id: string }) => tag.id);
  }, [currentTags]);

  // Calculate if there are more than 4 tags
  const hasMoreTags = useMemo(() => {
    return currentTags.length > 4;
  }, [currentTags.length]);

  // Get tag names for backward compatibility
  const tags = useMemo(() => {
    return displayedTags;
  }, [displayedTags]);

  // One read now backs both the card id and its tags, so one loading flag.
  const isLoading = isLoadingCard;

  // Handle tag changes using Apollo mutations
  const handleTagsChanged = useCallback(
    async (tagIds?: string[]) => {
      if (!tagIds || !boardId) {
        console.error("[usePlayerCardBase] No tagIds or boardId provided");
        return;
      }

      const currentIds = currentTagIds;
      const tagsToAdd = tagIds.filter((id: string) => !currentIds.includes(id));
      const tagsToRemove = currentIds.filter(
        (id: string) => !tagIds.includes(id)
      );

      // Apollo mutations with optimistic responses - no manual state
      if (tagsToAdd.length > 0) {
        await addTagsToPlayer(playerId, tagsToAdd);
      }
      if (tagsToRemove.length > 0) {
        await removeTagsFromPlayer(playerId, tagsToRemove);
      }

      // Notify parent component if needed (backward compatibility)
      if (tagsToAdd.length > 0 && onTagAdded) {
        const firstAddedTag = currentTags.find((tag: { id: string }) =>
          tagsToAdd.includes(tag.id)
        );
        if (firstAddedTag) {
          onTagAdded(playerId, (firstAddedTag as { name: string }).name);
        }
      }
    },
    [
      playerId,
      boardId,
      currentTagIds,
      currentTags,
      addTagsToPlayer,
      removeTagsFromPlayer,
      onTagAdded,
    ]
  );

  // Event handlers
  const handlePress = useCallback(() => {
    console.log(
      "🎯 [PLAYER CARD BASE] handlePress called, forwarding to onPress",
      !!onPress
    );
    onPress?.();
  }, [onPress]);

  const handleDragStart = useCallback(
    (dragData?: unknown, event?: unknown) => {
      onDragStart?.(dragData, event);
    },
    [onDragStart]
  );

  const handleDragEnd = useCallback(() => {
    onDragEnd?.();
  }, [onDragEnd]);

  const handleAddTag = useCallback(() => {
    if (!canEdit) {
      console.log(
        "[usePlayerCardBase] User cannot edit - tag selector disabled"
      );
      return;
    }
    setShowTagSelector(true);
  }, [canEdit]);

  const handleRemoveTag = useCallback(
    (tagName: string) => {
      if (!canEdit) {
        console.log(
          "[usePlayerCardBase] User cannot edit - tag removal disabled"
        );
        return;
      }

      console.log(
        `🔄 [usePlayerCardBase] Delegating tag removal to component-level handler for "${tagName}"`
      );

      if (!boardId) {
        console.error(
          "❌ [usePlayerCardBase] No boardId available for tag removal"
        );
        return;
      }

      // Find the tag by name to get its ID
      const tagToRemove = currentTags.find(
        (tag: { name: string }) => tag.name === tagName
      );

      if (!tagToRemove) {
        console.error(
          `❌ [usePlayerCardBase] Tag "${tagName}" not found for player ${playerId}`
        );
        return;
      }

      const typedTagToRemove = tagToRemove as { id: string; name: string };

      console.log(
        `🎯 [usePlayerCardBase] Starting background sync for tag removal:`,
        {
          tagId: typedTagToRemove.id,
          tagName: typedTagToRemove.name,
          playerId,
          approach: "BACKGROUND_SYNC_ONLY",
        }
      );

      // Background sync via Apollo mutation
      removeTagsFromPlayer(playerId, [typedTagToRemove.id]).catch(error => {
        console.warn(
          `⚠️ [usePlayerCardBase] Background removal failed:`,
          error
        );
      });

      console.log(
        `🔄 [usePlayerCardBase] Background sync initiated for tag "${tagName}"`
      );
    },
    [playerId, boardId, canEdit, currentTags, removeTagsFromPlayer]
  );

  const handleToggleTagExpansion = useCallback((index: number) => {
    setExpandedTagIndex(prev => (prev === index ? null : index));
  }, []);

  const handleToggleShowAllTags = useCallback(() => {
    setShowAllTags(prev => !prev);
  }, []);

  // Populate fallback colors when board tags are loaded
  // This ensures colors from any viewed board are available as fallbacks on other boards
  useEffect(() => {
    if (Object.keys(boardTagColors).length > 0) {
      populateFallbackColors(boardTagColors);
    }
  }, [boardTagColors]);

  return {
    displayedTags,
    currentTagIds,
    expandedTagIndex,
    showAllTags,
    hasMoreTags,
    tags,
    showTagSelector,
    setShowTagSelector,
    handlePress,
    handleAddTag,
    handleRemoveTag,
    handleTagsChanged,
    handleToggleTagExpansion,
    handleToggleShowAllTags,
    handleDragStart,
    handleDragEnd,
    isLoading,
    error,
  };
};
